/* eslint-disable no-shadow */
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import {
  getMultipleListVenues,
  getMultipleListVenuesBySlug,
  resetListById,
} from '@resy/ui-kitchen';

import {
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
  setMultiStepModalMode,
} from '../../../../../ui-kitchen/src/reducers';

import { deleteFollowListBySlug, setFollowListBySlug } from '../../../../../ui-kitchen/src/api/api';
import transformListCreatedBy from '../../../../../ui-kitchen/src/transformers/transformListCreatedBy';
import { MODAL_MODES, addVenueToListThunk } from '../../../../../ui-kitchen/src/reducers/favoriteSlice';
import MultipleListsDetailView from '../../../../../ui-kitchen/src/components/Account/MultipleListsDetailView/MultipleListsDetailView';
import Loader from '../../../../../ui-kitchen/src/components/Loader';
import { selectIsAuthenticated } from '../../../../../ui-kitchen/src/reducers/authTokenSlice';
import Logger from '../../../../../ui-kitchen/src/utils/logger';

const VENUE_LIMIT = 15;

const MultipleListsDetailContainer = ({
  $rootScope,
  $routeParams,
  Analytics,
  getMultipleListVenues,
  getMultipleListVenuesBySlug,
  isAuthenticated,
  isMobile,
  lists,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
  setMultiStepModalMode,
  showAuthModal,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isFollowing, setIsFollowing] = useState(undefined);
  const { listId, urlSlug } = $routeParams;

  const handleMoreVenues = async () => {
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 300) {
      if (lists === undefined) return;

      const listById = lists[listId || urlSlug];
      if (!listById) return;
      const { venues } = listById.results;
      const activeVenues = venues.filter((venue) => (venue.favorite));

      if (!isLoading && activeVenues.length < listById.list.count) {
        setIsLoading(true);

        if (urlSlug) {
          await getMultipleListVenues(
            {
              slug: urlSlug,
              limit: venues.length + VENUE_LIMIT,
              offset: 0,
            }
          );
        } else if (listId) {
          await getMultipleListVenues(
            {
              listId,
              limit: venues.length + VENUE_LIMIT,
              offset: 0,
            }
          );
        }

        setIsLoading(false);
      }
    }
  };

  const setDefaultFollowingState = (following) => {
    if (isFollowing === undefined) {
      setIsFollowing(following);
    }
  };

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        if (isAuthenticated) {
          if (urlSlug) {
            await getMultipleListVenuesBySlug({
              slug: urlSlug,
              limit: VENUE_LIMIT,
              offset: 0,
            });
          } else if (listId) {
            await getMultipleListVenues({
              listId,
              limit: 100,
              offset: 0,
            });
          }
        }
      } catch (err) {
        Logger.error('[MultipleListsDetailContainer] Error fetching venues on mount', err);
      }
    };
    fetchVenues();
  }, [isAuthenticated, urlSlug, listId]);

  useEffect(() => {
    window.addEventListener('scroll', handleMoreVenues);
    return () => {
      window.removeEventListener('scroll', handleMoreVenues);
    };
  }, [handleMoreVenues]);

  useEffect(() => {
    const listById = lists[urlSlug];
    if (listById && urlSlug) {
      setDefaultFollowingState(listById.is_followed);
    }
  }, [lists, urlSlug]);

  const handleCopyListClick = async () => {
    const showMultiListModal = () => {
      setMultiStepModalMode(MODAL_MODES.COPY_LIST);
      setMultiStepModalOpenState(true);
    };

    if (isAuthenticated) {
      showMultiListModal();
    } else {
      $rootScope.$broadcast('MODAL', 'signin', { successCallback: showMultiListModal });
    }
  };

  const handleFavoriteClick = async ({ venue_id: venueId, venue }) => {
    const showMultiListModal = () => {
      setMultiStepModalVenueContext({ name: venue?.name, city: venue?.location?.locality });
      setMultiStepModalVenueId(venueId);
      setMultiStepModalMode(MODAL_MODES.ADD_TO_LIST);
      setMultiStepModalOpenState(true);
    };

    if (isAuthenticated) {
      showMultiListModal();
    } else {
      showAuthModal();
    }
  };

  const handleFollowListClick = async (follow) => {
    if (urlSlug) {
      if (follow) {
        try {
          await setFollowListBySlug(urlSlug);
          setIsFollowing(true);
          Analytics.event('follow list');
        } catch (e) {
          if (e.status === 400) { // the list has already been followed
            setIsFollowing(true);
          }
          Logger.error('[MultipleListsDetailContainer] Follow List Error:', e);
        }
      } else {
        try {
          await deleteFollowListBySlug(urlSlug);
          setIsFollowing(false);
        } catch (e) {
          if (e.status === 400) { // the list has already been removed
            setIsFollowing(false);
          }
          Logger.error('[MultipleListsDetailContainer] Remove Following Error:', e);
        }
      }
    }
  };

  const list = lists[listId || urlSlug];

  if (list === undefined) {
    return (
      <MultipleListsDetailView
        isLoading
      />
    );
  }

  if (list === null) {
    return (
      <MultipleListsDetailView
        isError
        listId={$routeParams.listId}
      />
    );
  }

  setTimeout(() => {
    $rootScope.metadata.title = `Lists - ${list.list?.name}`;
  }, 100);

  return (
    <>
      <MultipleListsDetailView
        $rootScope={$rootScope}
        isFollowing={isFollowing}
        Analytics={Analytics}
        onCopyListClick={handleCopyListClick}
        onFavoriteClick={handleFavoriteClick}
        onFollowListClick={handleFollowListClick}
        name={list.list?.name}
        description={list.list?.description}
        createdBy={list.created_by && transformListCreatedBy(list.created_by)}
        count={list.list?.count}
        listId={list.list?.list_id}
        urlSlug={urlSlug}
        isLegacyHitlist={list.list?.is_legacy_hitlist}
        responsiveImages={list.list?.responsive_images}
        venues={list.results.venues}
        isMobile={isMobile}
        event={Analytics.event}
      />
      {isLoading && (<Loader containerHeight="100px" />)}
    </>
  );
};

MultipleListsDetailContainer.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
    metadata: PropTypes.shape({
      title: PropTypes.string,
    }),
  }).isRequired,
  $routeParams: PropTypes.shape({
    listId: PropTypes.string,
    urlSlug: PropTypes.string,
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  getMultipleListVenues: PropTypes.func.isRequired,
  getMultipleListVenuesBySlug: PropTypes.func.isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  lists: PropTypes.shape({}),
  setMultiStepModalOpenState: PropTypes.func.isRequired,
  setMultiStepModalVenueId: PropTypes.func.isRequired,
  setMultiStepModalVenueContext: PropTypes.func.isRequired,
  setMultiStepModalMode: PropTypes.func.isRequired,
  showAuthModal: PropTypes.func.isRequired,
};

MultipleListsDetailContainer.defaultProps = {
  isMobile: false,
  lists: undefined,
};

const mapStateToProps = (state) => {
  const { favorite } = state;
  return {
    isAuthenticated: selectIsAuthenticated(state),
    lists: favorite.listById,
  };
};

const mapDispatchToProps = {
  addVenueToListThunk,
  getMultipleListVenues,
  getMultipleListVenuesBySlug,
  resetListById,
  setMultiStepModalOpenState,
  setMultiStepModalVenueId,
  setMultiStepModalVenueContext,
  setMultiStepModalMode,
};

MultipleListsDetailContainer.displayName = 'MultipleListsDetailContainer';
export { mapStateToProps, MultipleListsDetailContainer as ComponentUnderTest };
export default connect(mapStateToProps, mapDispatchToProps)(MultipleListsDetailContainer);
